import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Trash } from "@phosphor-icons/react";
import { SiteNav } from "../components/SiteNav";
import { ForgetMeNotIcon } from "../components/ForgetMeNotIcon";
import { api, formatApiErrorDetail } from "../lib/api";
import { WelcomeUploader } from "../components/WelcomeUploader";
import { toast } from "sonner";

export default function ProfileDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [counts, setCounts] = useState({});
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [p, c] = await Promise.all([
                    api.get(`/profiles/${id}`),
                    api.get(`/categories`),
                ]);
                if (!active) return;
                setProfile(p.data.profile);
                setCounts(p.data.category_counts || {});
                setCategories(c.data.categories || []);
            } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load profile.");
                navigate("/dashboard");
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [id, navigate]);

    const groups = useMemo(() => {
        const g = {};
        for (const c of categories) {
            if (!g[c.group]) g[c.group] = [];
            g[c.group].push(c);
        }
        return g;
    }, [categories]);

    async function handleDelete() {
        if (!confirm(`Delete ${profile.subject_name}'s remembr? This cannot be undone.`)) return;
        try {
            await api.delete(`/profiles/${id}`);
            toast.success("Profile deleted.");
            navigate("/dashboard");
        } catch (err) {
            toast.error("Could not delete.");
        }
    }

    if (loading || !profile) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]" data-testid="profile-loading">
                <ForgetMeNotIcon size={48} className="animate-pulse" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FDFBF7]" data-testid="profile-detail-page">
            <SiteNav />
            <main className="pt-28 pb-24 px-6 rem-watercolor-bg min-h-screen">
                <div className="max-w-6xl mx-auto">
                    <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#5C5A59] hover:text-[#6B7DB8] mb-8" data-testid="profile-back-dashboard">
                        <ArrowLeft size={16} /> All remembrs
                    </Link>

                    {/* Profile header */}
                    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="rem-greeting-card rounded-[2rem] p-8 sm:p-12 text-center mb-12" data-testid="profile-header">
                        <div className="flex justify-center mb-5"><ForgetMeNotIcon size={44} /></div>
                        <p className="font-serif text-xl text-[#2D2B2A]">Good morning,</p>
                        <h1 className="font-serif text-4xl sm:text-5xl text-[#4A5A95] italic mt-1 leading-tight" data-testid="profile-greeting-name">
                            {profile.subject_name}
                            {profile.subject_name_2 && (<> & {profile.subject_name_2}</>)},<br/>
                            let's reminisce
                        </h1>
                        {(profile.birth_year || profile.birth_place) && (
                            <p className="mt-5 text-sm text-[#8A8786]">
                                {profile.birth_place ? `Born ${profile.birth_place}` : ""}
                                {profile.birth_place && profile.birth_year ? " · " : ""}
                                {profile.birth_year || ""}
                            </p>
                        )}
                        {profile.bio && (
                            <p className="mt-6 max-w-xl mx-auto text-[#5C5A59] italic font-serif text-lg leading-relaxed">"{profile.bio}"</p>
                        )}
                        <div className="mt-6 inline-flex flex-wrap items-center gap-3 justify-center">
                            <Link
                                to={`/profiles/${id}/reminisce`}
                                className="rem-start-button inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#6B7DB8] hover:bg-[#4A5A95] text-white text-sm font-medium transition-all"
                                data-testid="profile-open-reminisce"
                            >
                                ▶ Open remembr Mode
                            </Link>
                            <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/70 border border-[#E5E1D8]/60">
                                <span className="text-sm text-[#5C5A59]">
                                    <span className="font-medium text-[#4A5A95]">{profile.memory_count || 0}</span> {(profile.memory_count || 0) === 1 ? "memory" : "memories"} gathered
                                </span>
                            </div>
                        </div>
                    </motion.div>

                    {/* Custom welcome — record up to 30s of family voice + photo */}
                    <WelcomeUploader profile={profile} onSaved={(updated) => setProfile(updated)} />

                    {/* Category groups */}
                    {Object.entries(groups).map(([group, cats], gi) => (
                        <motion.section
                            key={group}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: gi * 0.05 }}
                            className="mb-14"
                            data-testid={`category-group-${group.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        >
                            <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#6B7DB8] font-semibold mb-5">{group}</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {cats.map((cat) => {
                                    const count = counts[cat.slug] || 0;
                                    return (
                                        <Link
                                            key={cat.slug}
                                            to={`/profiles/${id}/chapter/${cat.slug}`}
                                            className="group bg-white rounded-2xl border border-[#E5E1D8]/60 hover:border-[#8C9ECB]/60 hover:shadow-rem-soft p-5 flex items-center justify-between transition-all hover:-translate-y-0.5"
                                            data-testid={`category-${cat.slug}`}
                                        >
                                            <div>
                                                <h3 className="font-serif text-lg text-[#2D2B2A]">{cat.label}</h3>
                                                <p className="text-xs text-[#8A8786] mt-1">
                                                    {count === 0 ? "Begin this chapter" : `${count} ${count === 1 ? "memory" : "memories"}`}
                                                </p>
                                            </div>
                                            <ArrowRight size={18} className="text-[#8C9ECB] opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </Link>
                                    );
                                })}
                            </div>
                        </motion.section>
                    ))}

                    <div className="mt-16 text-center">
                        <button onClick={handleDelete} className="inline-flex items-center gap-2 text-xs text-[#8A8786] hover:text-red-600 transition-colors" data-testid="profile-delete">
                            <Trash size={14} /> Delete this remembr
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
}
